/**
 * gameAudio/feedbackVoice.ts — AI-generated feedback voice lines for quiz +
 * mini-games (correct / wrong / finish reward / finish keep-trying), per lang.
 * PR-16 split from lib/gameAudio.ts.
 *
 * The generated mp3s live under /sounds/feedback/{zh,en}/ and are produced by
 * tools/generate_feedback_tts_assets.mjs via the same AI server TTS endpoint
 * used for quiz pronunciation. Runtime only reads the static manifest at
 * /sounds/feedback/manifest.json and the cached mp3s, so every phrase reuses
 * the browser HTTP cache.
 *
 * Per-kind cooldown: rapid wrong-wrong-wrong in a mini-game would otherwise
 * stack the voice line on top of itself; the lastFeedbackPlayedAt map
 * throttles a given (kind, lang) pair to at most one play per cooldownMs.
 * Callers pass cooldownMs: 0 to bypass (e.g. playPraise after a quiz win
 * already plays the fanfare and wants the voice immediately).
 *
 * Anti-repeat: pickFeedbackItem prefers a different item from the last one
 * played for the same (kind, lang) so the child hears variety even with
 * only 2-3 recordings per kind.
 *
 * playPraise composes a synth fanfare (rising arpeggios + shimmer) with the
 * quiz_correct feedback voice so there is always celebratory audio even if
 * the recording is still loading.
 *
 * 调用关系：
 *   - QuizPage, MathBlitzPage, AgeMiniGamePage, MemoryDigitsPage,
 *     PotionPuzzlePage, OnboardingQuizPage call playFeedbackVoice + playPraise.
 *   - AppShellV2 calls preloadFeedbackVoices once the lang is known.
 *   - Synth helpers + shared sample cache come from ./_context.
 *
 * 关键约束：
 *   - Manifest fetch is idempotent — once loaded the items are cached for the
 *     session. A failed fetch flips into empty-items mode so callers no-op
 *     gracefully instead of hammering the network.
 *   - audio.decodeAudioData runs through the shared loadSample, so the
 *     buffer cache is hot if the kid already heard the line.
 */

import { getLang, type Lang } from '../../i18n';
import {
  arpeggio,
  ensureAudio,
  getAudioEnabled,
  getSfxGain,
  loadSample,
  sampleBufferCache,
  tone,
} from './_context';

export type FeedbackVoiceKind =
  | 'quiz_correct'
  | 'quiz_wrong'
  | 'game_correct'
  | 'game_wrong'
  | 'game_finish_reward'
  | 'game_finish_keep_trying';

interface FeedbackVoiceItem {
  /** Unique id from the generator — stable across rebuilds for a given line. */
  id: string;
  /** Which kind of feedback this line belongs to. */
  kind: FeedbackVoiceKind;
  /** Recording language; we prefer items matching the active UI lang. */
  lang: Lang;
  /** The original text the TTS was generated from (debug only). */
  text: string;
  /** Public URL under /sounds/feedback/<lang>/...mp3. */
  audio_url: string;
}

interface FeedbackVoiceManifest {
  items?: FeedbackVoiceItem[];
}

const FEEDBACK_MANIFEST_URL = '/sounds/feedback/manifest.json';
const FEEDBACK_GAIN = 0.88;
// Default throttle: rapid taps in a mini-game can fire correct/wrong many
// times a second; 900ms keeps the voice line audible without stacking.
const FEEDBACK_DEFAULT_COOLDOWN_MS = 900;

let feedbackManifestPromise: Promise<FeedbackVoiceItem[]> | null = null;
let feedbackVoiceItems: FeedbackVoiceItem[] | null = null;
const lastFeedbackItemByKey = new Map<string, string>();
const lastFeedbackPlayedAt = new Map<string, number>();
const feedbackPreloadStarted = new Set<Lang>();

async function loadFeedbackManifest(): Promise<FeedbackVoiceItem[]> {
  if (feedbackVoiceItems) return feedbackVoiceItems;
  if (feedbackManifestPromise) return feedbackManifestPromise;
  feedbackManifestPromise = (async () => {
    try {
      const res = await fetch(FEEDBACK_MANIFEST_URL, { cache: 'force-cache' });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const json = await res.json() as FeedbackVoiceManifest;
      feedbackVoiceItems = (json.items || []).filter((item): item is FeedbackVoiceItem => (
        !!item
        && typeof item.id === 'string'
        && typeof item.kind === 'string'
        && (item.lang === 'zh' || item.lang === 'en')
        && typeof item.audio_url === 'string'
      ));
      return feedbackVoiceItems;
    } catch {
      feedbackVoiceItems = [];
      return feedbackVoiceItems;
    }
  })();
  return feedbackManifestPromise;
}

function pickFeedbackItem(kind: FeedbackVoiceKind, lang: Lang): FeedbackVoiceItem | null {
  const items = feedbackVoiceItems || [];
  const exact = items.filter((item) => item.kind === kind && item.lang === lang);
  const candidates = exact.length ? exact : items.filter((item) => item.kind === kind);
  if (!candidates.length) return null;
  const key = `${kind}:${lang}`;
  const lastId = lastFeedbackItemByKey.get(key);
  const pool = candidates.length > 1
    ? candidates.filter((item) => item.id !== lastId)
    : candidates;
  const item = pool[Math.floor(Math.random() * pool.length)] || candidates[0];
  lastFeedbackItemByKey.set(key, item.id);
  return item;
}

function playDecodedSample(buf: AudioBuffer, gain = FEEDBACK_GAIN): boolean {
  const audio = ensureAudio();
  const sfx = getSfxGain();
  if (!audio || !sfx) return false;
  if (audio.state === 'suspended') void audio.resume();
  const src = audio.createBufferSource();
  const env = audio.createGain();
  src.buffer = buf;
  env.gain.value = gain;
  src.connect(env);
  env.connect(sfx);
  src.start();
  return true;
}

function shouldThrottleFeedback(kind: FeedbackVoiceKind, lang: Lang, cooldownMs: number): boolean {
  if (cooldownMs <= 0) return false;
  const key = `${kind}:${lang}`;
  const now = Date.now();
  const last = lastFeedbackPlayedAt.get(key) || 0;
  if (now - last < cooldownMs) return true;
  lastFeedbackPlayedAt.set(key, now);
  return false;
}

export function preloadFeedbackVoices(lang: Lang = getLang()) {
  if (typeof window === 'undefined') return;
  void loadFeedbackManifest().then((items) => {
    if (!getAudioEnabled()) return;
    if (feedbackPreloadStarted.has(lang)) return;
    feedbackPreloadStarted.add(lang);
    const preferred = items.filter((item) => item.lang === lang);
    for (const item of preferred) void loadSample(item.audio_url);
  });
}

export function playFeedbackVoice(
  kind: FeedbackVoiceKind,
  lang: Lang = getLang(),
  options: { cooldownMs?: number; gain?: number } = {},
): boolean {
  if (!getAudioEnabled()) return false;
  const cooldownMs = options.cooldownMs ?? FEEDBACK_DEFAULT_COOLDOWN_MS;
  if (shouldThrottleFeedback(kind, lang, cooldownMs)) return false;
  if (!feedbackVoiceItems) {
    void loadFeedbackManifest().then((items) => {
      if (items.length) playFeedbackVoice(kind, lang, { ...options, cooldownMs: 0 });
    });
    return false;
  }
  const item = pickFeedbackItem(kind, lang);
  if (!item) return false;
  const cached = sampleBufferCache.get(item.audio_url);
  if (cached) return playDecodedSample(cached, options.gain ?? FEEDBACK_GAIN);
  void loadSample(item.audio_url).then((buf) => {
    if (buf && getAudioEnabled()) playDecodedSample(buf, options.gain ?? FEEDBACK_GAIN);
  });
  return false;
}

function playPraiseFanfare() {
  // Brighter, longer than playCue('success') — three rising arpeggios with a
  // sparkle on top so the moment feels like a real celebration.
  arpeggio([523.25, 659.25, 783.99, 1046.5], 0.06, 0.06, 0.058);
  arpeggio([1318.51, 1567.98], 0.08, 0.08, 0.045);
  tone(2093, 0.18, 'sine', 0.022, 0.32);  // shimmering bell on top
  tone(1567.98, 0.12, 'triangle', 0.018, 0.42);
}

/**
 * Play a celebratory praise sound for a successful action (e.g. quiz correct).
 * Combines the local AI-generated voice with a short fanfare bed.
 */
export function playPraise(lang: Lang = getLang()) {
  if (!getAudioEnabled()) return;
  // Always layer the fanfare so we have a baseline even when speech /
  // recorded voice is still loading.
  playPraiseFanfare();
  playFeedbackVoice('quiz_correct', lang, { cooldownMs: 0, gain: 0.92 });
}
